'use client';

import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { type JSONContent } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Typography from '@tiptap/extension-typography';
import { common, createLowlight } from 'lowlight';
import { MermaidCodeBlock } from '../lib/tiptap/extensions/MermaidCodeBlock';
import { TiptapViewerSkeleton } from './TiptapViewerSkeleton';
import { EntityReference } from '../lib/tiptap/extensions/EntityReference';

const lowlight = createLowlight(common);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const buildContent = (rawContent: string): JSONContent => {
  try {
    const parsed = JSON.parse(rawContent) as JSONContent;
    if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
      return parsed;
    }
  } catch {
    return {
      type: 'doc',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: rawContent }] }],
    };
  }

  return {
    type: 'doc',
    content: [{ type: 'paragraph' }],
  };
};

const estimateNodeWeight = (node: JSONContent | null | undefined): number => {
  if (!node) {
    return 0;
  }

  const childWeight = (node.content ?? []).reduce((total, child) => total + estimateNodeWeight(child), 0);

  switch (node.type) {
    case 'heading':
      return 2 + childWeight;
    case 'codeBlock':
    case 'blockquote':
      return 4 + childWeight;
    case 'table':
      return 6 + childWeight;
    case 'image':
      return 8;
    case 'bulletList':
    case 'orderedList':
    case 'taskList':
      return 2 + childWeight;
    case 'listItem':
    case 'taskItem':
      return 1 + childWeight;
    case 'paragraph': {
      const text = (node as { text?: string }).text ?? '';
      return Math.max(1, Math.ceil((text.length || childWeight) / 80));
    }
    default:
      return Math.max(1, childWeight);
  }
};

const estimateViewerMinHeight = (content: JSONContent) => {
  const totalWeight = estimateNodeWeight(content);
  return clamp(144 + totalWeight * 20, 192, 720);
};

type TiptapViewerProps = {
  content: string;
  onQuoteSelect?: (text: string) => void;
  minHeight?: number;
};

export const TiptapViewer = ({ content, onQuoteSelect, minHeight: minHeightOverride }: TiptapViewerProps) => {
  const editorContent = useMemo(() => buildContent(content), [content]);
  const appliedContentRef = useRef(content);
  const minHeight = useMemo(
    () => minHeightOverride ?? estimateViewerMinHeight(editorContent),
    [editorContent, minHeightOverride],
  );

  const editor = useEditor({
    immediatelyRender: false,
    editable: false,
    extensions: [
      StarterKit.configure({ codeBlock: false, link: false }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({ openOnClick: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      MermaidCodeBlock.configure({ lowlight }),
      Image.configure({ allowBase64: true }),
      TextStyle,
      Color,
      Typography,
      EntityReference,
    ],
    content: editorContent,
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    if (appliedContentRef.current === content) {
      return;
    }

    appliedContentRef.current = content;

    queueMicrotask(() => {
      if (!editor.isDestroyed) {
        editor.commands.setContent(editorContent, { emitUpdate: false });
      }
    });
  }, [content, editor, editorContent]);

  if (!editor) {
    return <TiptapViewerSkeleton minHeight={minHeight} />;
  }

  // minHeight는 마운트 전 스켈레톤의 레이아웃 예약(anti-CLS)용이다. 콘텐츠가 렌더된 뒤에도 예약값을
  // 강제하면 추정이 실제보다 클 때 하단에 죽은 여백이 남으므로, 마운트된 뷰어 높이는 콘텐츠에 맡긴다.
  return (
    <div className="relative">
      {onQuoteSelect && editor ? (
        <BubbleMenu
          editor={editor}
          shouldShow={({ state, view }) => {
            const { from, to } = state.selection;
            if (from !== to) {
              return true;
            }
            const root = view.dom.ownerDocument ?? document;
            const domSelection = root.getSelection();
            if (!domSelection || domSelection.isCollapsed || !domSelection.rangeCount) {
              return false;
            }
            const range = domSelection.getRangeAt(0);
            return view.dom.contains(range.commonAncestorContainer);
          }}
        >
          <div className="flex items-center gap-0.5 rounded-lg border border-base-300 bg-base-100 p-1 shadow-lg">
            <button
              type="button"
              className="btn btn-ghost btn-xs gap-1"
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onPointerDown={(event) => {
                event.preventDefault();
              }}
              onClick={(event) => {
                event.preventDefault();
                const domSelection = window.getSelection();
                const domText = domSelection?.toString().trim() ?? '';
                const proseText = editor.state.doc
                  .textBetween(editor.state.selection.from, editor.state.selection.to, '\n')
                  .trim();
                const selectedText = domText || proseText;
                if (selectedText) {
                  onQuoteSelect(selectedText);
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              Quote
            </button>
          </div>
        </BubbleMenu>
      ) : null}
      <EditorContent editor={editor} className="tiptap !min-h-0 !rounded-none !border-0 !p-0 tiptap-viewer" />
    </div>
  );
};
